import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Shield, Mail, Camera, Trash2, Laptop, X as XIcon, KeyRound, Plus, Fingerprint } from "lucide-react";
import {
  registerPasskey, listMyPasskeys, deleteMyPasskey, isWebAuthnSupported,
} from "@/lib/passkey";
import UserAvatar from "@/components/UserAvatar";

export default function ProfilePage() {
  const { user, refreshMe } = useAuth();
  const [secret, setSecret] = useState(null);
  const [otpUri, setOtpUri] = useState(null);
  const [code, setCode] = useState("");
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [maxDays, setMaxDays] = useState(90);
  const [devicesReload, setDevicesReload] = useState(0);
  const [passkeys, setPasskeys] = useState([]);
  const [pkReload, setPkReload] = useState(0);
  const [pkRegistering, setPkRegistering] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listMyPasskeys();
        if (!cancelled) setPasskeys(items);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [pkReload]);

  const addPasskey = async () => {
    const label = window.prompt("Name this passkey (e.g. 'iPhone Face ID', 'MacBook Touch ID')", "My device") || "My device";
    setPkRegistering(true);
    try {
      await registerPasskey(label);
      toast.success("Passkey added — you can now sign in with Face ID / Touch ID.");
      setPkReload((k) => k + 1);
    } catch (e) {
      toast.error(e?.message || formatApiError(e));
    } finally {
      setPkRegistering(false);
    }
  };

  const removePasskey = async (id) => {
    if (!window.confirm("Remove this passkey? You can re-add it later.")) return;
    try {
      await deleteMyPasskey(id);
      toast.success("Passkey removed");
      setPkReload((k) => k + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/trusted-devices");
        if (cancelled) return;
        setDevices(data?.items || []);
        setMaxDays(data?.max_days || 90);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [devicesReload]);

  const reloadDevices = () => setDevicesReload((k) => k + 1);

  const revokeDevice = async (id) => {
    try {
      await api.delete(`/auth/trusted-devices/${id}`);
      toast.success("Device removed");
      reloadDevices();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revokeAllDevices = async () => {
    if (!window.confirm('Forget all remembered browsers? You will need to enter your 2FA code on next sign-in everywhere.')) return;
    try {
      await api.post("/auth/trusted-devices/revoke-all");
      toast.success("All remembered devices forgotten");
      reloadDevices();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startTotp = async () => {
    try { const { data } = await api.post("/users/me/2fa/setup"); setSecret(data.secret); setOtpUri(data.otpauth_uri); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const enableTotp = async () => {
    if (code.length < 6) return toast.error("Enter 6-digit code");
    try { await api.post("/users/me/2fa/enable", { code }); toast.success("TOTP 2FA enabled"); setSecret(null); setOtpUri(null); setCode(""); refreshMe(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const enableEmailOtp = async () => {
    try { await api.post("/users/me/2fa/enable-email"); toast.success("Email 2FA enabled"); refreshMe(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const disable = async () => {
    try { await api.post("/users/me/2fa/disable"); toast.success("2FA disabled"); refreshMe(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be 2 MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Profile picture updated");
      if (fileRef.current) fileRef.current.value = "";
      refreshMe();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const removeAvatar = async () => {
    try { await api.delete("/users/me/avatar"); toast.success("Profile picture removed"); refreshMe(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <div className="tiny-label">Your account</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Profile & security</h1>
      </div>

      <Card className="border-border shadow-none">
        <CardContent className="p-6 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Full name</Label><Input readOnly value={user?.full_name || ""} /></div>
            <div><Label>Email</Label><Input readOnly value={user?.email || ""} /></div>
            <div><Label>Role</Label><Input readOnly value={user?.role?.replace("_", " ") || ""} className="capitalize" /></div>
            <div><Label>MFA</Label><Input readOnly value={user?.mfa_enabled ? `Enabled (${user?.mfa_method})` : "Disabled"} /></div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2"><Camera className="w-4 h-4" /><h3 className="font-display font-semibold text-lg">Profile picture</h3></div>
          <div className="flex items-center gap-5 flex-wrap">
            <UserAvatar user={user} size={72} />
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={uploadAvatar}
                className="hidden"
                data-testid="avatar-file-input"
              />
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="avatar-upload-btn">
                  <Camera className="w-4 h-4 mr-1" /> {uploading ? "Uploading…" : (user?.has_avatar ? "Change picture" : "Upload picture")}
                </Button>
                {user?.has_avatar && (
                  <Button variant="outline" onClick={removeAvatar} data-testid="avatar-remove-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
              <div className="text-xs text-muted-foreground">PNG, JPEG, WebP or GIF. Max 2 MB.</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2"><Shield className="w-4 h-4" /><h3 className="font-display font-semibold text-lg">Two-factor authentication</h3></div>
          {user?.mfa_enabled ? (
            <>
              <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800"><AlertDescription>2FA is enabled on your account ({user.mfa_method}).</AlertDescription></Alert>
              <Button variant="outline" onClick={disable} data-testid="disable-2fa">Disable 2FA</Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Choose a method — authenticator app (recommended) or email OTP.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="border-border"><CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2"><Shield className="w-4 h-4" /><div className="font-medium">Authenticator app (TOTP)</div></div>
                  {!secret
                    ? <Button onClick={startTotp} data-testid="start-totp">Set up authenticator</Button>
                    : <div className="space-y-3">
                        <div className="text-xs text-muted-foreground">Scan this URL in your authenticator, or enter the secret manually:</div>
                        <div className="p-2 rounded bg-muted font-mono text-xs break-all">{otpUri}</div>
                        <div className="p-2 rounded bg-muted font-mono text-xs">Secret: {secret}</div>
                        <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="verify-totp-input">
                          <InputOTPGroup>
                            {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} />)}
                          </InputOTPGroup>
                        </InputOTP>
                        <Button onClick={enableTotp} data-testid="enable-totp">Verify & enable</Button>
                      </div>}
                </CardContent></Card>
                <Card className="border-border"><CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2"><Mail className="w-4 h-4" /><div className="font-medium">Email OTP</div></div>
                  <p className="text-xs text-muted-foreground">Requires SMTP to be configured. Codes are sent to your email on each sign-in.</p>
                  <Button variant="outline" onClick={enableEmailOtp} data-testid="enable-email-2fa">Enable email 2FA</Button>
                </CardContent></Card>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border-border shadow-none" data-testid="passkeys-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Fingerprint className="w-4 h-4" />
              <h3 className="font-display font-semibold text-lg">Passkeys (Face ID / Touch ID)</h3>
            </div>
            {isWebAuthnSupported() && (
              <Button size="sm" onClick={addPasskey} disabled={pkRegistering} data-testid="add-passkey-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> {pkRegistering ? "Adding…" : "Add passkey"}
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Sign in faster on this device using Face ID, Touch ID, or your built-in biometric.
            Once added, you can choose &ldquo;Sign in with a passkey&rdquo; on the login page — no password needed.
          </p>
          {!isWebAuthnSupported() && (
            <div className="p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900">
              This browser does not support passkeys. Try Safari on iOS / macOS or Chrome on Android.
            </div>
          )}
          {passkeys.length === 0 ? (
            <div className="p-4 rounded-md border border-dashed border-border text-center text-sm text-muted-foreground" data-testid="no-passkeys">
              No passkeys registered yet.
            </div>
          ) : (
            <div className="divide-y divide-border border border-border rounded-md">
              {passkeys.map((p) => {
                const created = p.created_at ? new Date(p.created_at).toLocaleDateString() : "—";
                const lastUsed = p.last_used_at ? new Date(p.last_used_at).toLocaleString() : "Never used";
                return (
                  <div key={p.id} className="flex items-start gap-3 p-3" data-testid={`passkey-${p.id}`}>
                    <KeyRound className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{p.name || "Passkey"}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Added {created} · {lastUsed}
                        {p.backup_eligible ? " · synced" : ""}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removePasskey(p.id)} data-testid={`remove-passkey-${p.id}`}>
                      <XIcon className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border shadow-none" data-testid="trusted-devices-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <Laptop className="w-4 h-4" />
              <h3 className="font-display font-semibold text-lg">Remembered devices</h3>
            </div>
            {devices.length > 0 && (
              <Button variant="ghost" size="sm" onClick={revokeAllDevices} data-testid="revoke-all-devices">
                Forget all
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Browsers you have ticked &ldquo;Remember this device&rdquo; on. They skip the 2FA prompt for {maxDays} days.
            Revoke any browser that is not yours.
          </p>
          {devices.length === 0 ? (
            <div className="p-4 rounded-md border border-dashed border-border text-center text-sm text-muted-foreground" data-testid="no-trusted-devices">
              No remembered devices.
            </div>
          ) : (
            <div className="divide-y divide-border border border-border rounded-md">
              {devices.map((d) => {
                const created = d.created_at ? new Date(d.created_at).toLocaleDateString() : "—";
                const lastSeen = d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : "—";
                const expires = d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "—";
                return (
                  <div key={d.id} className="flex items-start gap-3 p-3" data-testid={`trusted-device-${d.id}`}>
                    <Laptop className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate" title={d.user_agent || ""}>
                        {d.user_agent ? d.user_agent.slice(0, 80) : "Unknown browser"}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Trusted on {created} · Last seen {lastSeen} · Expires {expires}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => revokeDevice(d.id)} data-testid={`revoke-device-${d.id}`}>
                      <XIcon className="w-3.5 h-3.5 mr-1" /> Forget
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
